import * as React from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material';

import {
    DataGrid,
    GridColDef,
    GridRowId,
    GridRowSelectionModel
} from '@mui/x-data-grid';

import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import VisibilityIcon from '@mui/icons-material/Visibility';

import AdminLayout from '../components/AdminLayout';
import Api from '../services/Api';
import { SportsGolfOutlined } from '@mui/icons-material';

// -----------------------------
// DTOs según api.json
// -----------------------------

export interface IUserDto {
    id: number;
    name: string | null;
    email: string | null;
    role: string | null;
    isActive: boolean;
    createdAt: string; // date-time
}

interface ICreateUserRequest {
    name: string | null;
    email: string | null;
    role: string | null;
    password: string | null;
}

interface IUpdateUserRequest {
    name: string | null;
    role: string | null;
    isActive: boolean;
}

export interface IMiniGameDto {
    id: number;
    tournamentId: number;
    name: string | null;
    description: string | null;
    imageUrl: string | null;
    displayOrder: number;
    isActive: boolean;
}

export interface IUserMiniGameDto {
    id: number;
    userId: number;
    miniGameId: number;
    createdAt: string;
}

interface IAssignMiniGameRequest {
    userId: number;
    miniGameId: number;
}

// -----------------------------
// Diálogo alta / edición de usuario
// -----------------------------

interface IUserDialogProps {
    open: boolean;
    mode: 'create' | 'edit';
    user: IUserDto | null;
    onClose: () => void;
    onSaved: () => void;
}

interface IUserDialogState {
    name: string;
    email: string;
    role: string;
    password: string;
    isActive: boolean;
    saving: boolean;
    error: string | null;
}

class UserDialog extends React.Component<IUserDialogProps, IUserDialogState> {
    public constructor(props: IUserDialogProps) {
        super(props);

        const u = props.user;

        this.state = {
            name: u?.name || '',
            email: u?.email || '',
            role: u?.role || '',
            password: '',
            isActive: u?.isActive ?? true,
            saving: false,
            error: null
        };

        this.handleSave = this.handleSave.bind(this);
    }

    public componentDidUpdate(prevProps: IUserDialogProps): void {
        if (
            this.props.open &&
            (!prevProps.open ||
                prevProps.mode !== this.props.mode ||
                prevProps.user?.id !== this.props.user?.id)
        ) {
            const u = this.props.user;

            this.setState({
                name: u?.name || '',
                email: u?.email || '',
                role: u?.role || '',
                password: '',
                isActive: u?.isActive ?? true,
                saving: false,
                error: null
            });
        }
    }

    private validate(): string | null {
        const { mode } = this.props;
        const { name, email, password } = this.state;

        if (!name.trim()) return 'El nombre es obligatorio.';
        if (!email.trim()) return 'El email es obligatorio.';

        if (mode === 'create' && !password.trim()) {
            return 'La contraseña es obligatoria para crear un usuario.';
        }

        return null;
    }

    private handleFieldChange =
        (field: 'name' | 'email' | 'role' | 'password') =>
            (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                this.setState({
                    [field]: event.target.value
                } as Pick<IUserDialogState, typeof field>);
            };

    private handleToggleActive = (
        _event: React.ChangeEvent<HTMLInputElement>,
        checked: boolean
    ) => {
        this.setState({ isActive: checked });
    };

    private formatTitle(): string {
        return this.props.mode === 'create'
            ? 'Nuevo usuario'
            : 'Editar usuario';
    }

    private async handleSave(): Promise<void> {
        const error = this.validate();
        if (error) {
            this.setState({ error });
            return;
        }

        this.setState({ saving: true, error: null });

        try {
            const { mode, user } = this.props;
            const { name, email, role, password, isActive } = this.state;

            if (mode === 'create') {
                const body: ICreateUserRequest = {
                    name: name || null,
                    email: email || null,
                    role: role || null,
                    password: password || null
                };

                const response = await Api.post('/api/Users', body);
                if (!response.ok) {
                    throw new Error('No se pudo crear el usuario.');
                }
            } else {
                if (!user) {
                    throw new Error('No se ha especificado el usuario a editar.');
                }

                const body: IUpdateUserRequest = {
                    name: name || null,
                    role: role || null,
                    isActive
                };

                const response = await Api.put(`/api/Users/${user.id}`, body);
                if (!response.ok) {
                    throw new Error('No se pudo actualizar el usuario.');
                }
            }

            this.props.onSaved();
        } catch (err: any) {
            this.setState({
                saving: false,
                error: err.message || 'Error al guardar el usuario.'
            });
        }
    }

    public render(): React.ReactElement<IUserDialogProps> {
        const { open, onClose, mode } = this.props;
        const { name, email, role, password, isActive, saving, error } =
            this.state;

        const isEdit = mode === 'edit';

        return (
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle>{this.formatTitle()}</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {error && (
                            <Typography variant="body2" color="error">
                                {error}
                            </Typography>
                        )}

                        <TextField
                            label="Nombre"
                            fullWidth
                            value={name}
                            onChange={this.handleFieldChange('name')}
                            required
                        />

                        <TextField
                            label="Email"
                            fullWidth
                            value={email}
                            onChange={this.handleFieldChange('email')}
                            required
                            disabled={isEdit} // la API de Update no admite cambiar email
                        />

                        <TextField
                            label="Rol"
                            fullWidth
                            value={role}
                            onChange={this.handleFieldChange('role')}
                            placeholder="Administrador, Staff, etc."
                        />

                        {!isEdit && (
                            <TextField
                                label="Contraseña"
                                type="password"
                                fullWidth
                                value={password}
                                onChange={this.handleFieldChange('password')}
                                required
                            />
                        )}

                        {isEdit && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isActive}
                                        onChange={this.handleToggleActive}
                                        color="primary"
                                    />
                                }
                                label="Usuario activo"
                            />
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={saving}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => this.handleSave()}
                        disabled={saving}
                    >
                        {saving ? 'Guardando...' : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
}

// -----------------------------
// Diálogo Asignar juego
// -----------------------------

interface IAssignMiniGameDialogProps {
    open: boolean;
    user: IUserDto | null;
    onClose: () => void;
    onAssigned: () => void;
}

interface IAssignMiniGameDialogState {
    miniGames: IMiniGameDto[];
    userMiniGames: IUserMiniGameDto[];
    loading: boolean;
    error: string | null;
    selectedMiniGameId: number | '';
    saving: boolean;
}

class AssignMiniGameDialog extends React.Component<
    IAssignMiniGameDialogProps,
    IAssignMiniGameDialogState
> {
    public constructor(props: IAssignMiniGameDialogProps) {
        super(props);

        this.state = {
            miniGames: [],
            userMiniGames: [],
            loading: false,
            error: null,
            selectedMiniGameId: '',
            saving: false
        };

        this.loadData = this.loadData.bind(this);
        this.handleSave = this.handleSave.bind(this);
    }

    public componentDidUpdate(prevProps: IAssignMiniGameDialogProps): void {
        if (
            this.props.open &&
            (!prevProps.open ||
                prevProps.user?.id !== this.props.user?.id)
        ) {
            this.loadData();
        }
    }

    private async loadData(): Promise<void> {
        const user = this.props.user;
        if (!user) return;

        this.setState({
            loading: true,
            error: null,
            miniGames: [],
            userMiniGames: [],
            selectedMiniGameId: ''
        });

        try {
            const [mgResponse, umgResponse] = await Promise.all([
                Api.get('/api/MiniGames'),
                Api.get(`/api/UserMiniGames/by-user/${user.id}`)
            ]);

            if (!mgResponse.ok) {
                throw new Error('No se pudieron cargar los juegos.');
            }
            if (!umgResponse.ok) {
                throw new Error(
                    'No se pudieron cargar los juegos asignados al usuario.'
                );
            }

            const miniGames: IMiniGameDto[] = await mgResponse.json();
            const userMiniGames: IUserMiniGameDto[] =
                await umgResponse.json();

            // Ordenamos por displayOrder y nombre
            miniGames.sort((a, b) => {
                if (a.displayOrder !== b.displayOrder) {
                    return a.displayOrder - b.displayOrder;
                }
                const an = (a.name || '').toLowerCase();
                const bn = (b.name || '').toLowerCase();
                return an.localeCompare(bn);
            });

            this.setState({
                miniGames,
                userMiniGames,
                loading: false,
                error: null
            });
        } catch (error: any) {
            this.setState({
                loading: false,
                error:
                    error.message ||
                    'Error al cargar los datos de juegos.'
            });
        }
    }

    private handleMiniGameChange = (event: SelectChangeEvent): void => {
        const value = event.target.value;
        this.setState({
            selectedMiniGameId: value ? Number(value) : ''
        });
    };

    private async handleSave(): Promise<void> {
        const user = this.props.user;
        const { selectedMiniGameId } = this.state;

        if (!user) {
            this.setState({
                error: 'No se ha especificado el usuario.'
            });
            return;
        }

        if (selectedMiniGameId === '') {
            this.setState({
                error: 'Selecciona un juego.'
            });
            return;
        }

        this.setState({ saving: true, error: null });

        try {
            const body: IAssignMiniGameRequest = {
                userId: user.id,
                miniGameId: selectedMiniGameId
            };

            const response = await Api.post('/api/UserMiniGames', body);
            if (!response.ok) {
                throw new Error(
                    'No se pudo asignar el juego al usuario.'
                );
            }

            this.setState({ saving: false, error: null });

            this.props.onAssigned();
        } catch (error: any) {
            this.setState({
                saving: false,
                error:
                    error.message ||
                    'Error al asignar el juego al usuario.'
            });
        }
    }

    private getAvailableMiniGames(): IMiniGameDto[] {
        const { miniGames, userMiniGames } = this.state;
        if (!miniGames.length) return [];

        const assignedIds = new Set(
            userMiniGames.map((u) => u.miniGameId)
        );

        return miniGames.filter((mg) => !assignedIds.has(mg.id));
    }

    public render(): React.ReactElement<IAssignMiniGameDialogProps> {
        const { open, onClose, user } = this.props;
        const {
            loading,
            error,
            selectedMiniGameId,
            saving
        } = this.state;

        const availableMiniGames = this.getAvailableMiniGames();
        const canSave =
            !loading &&
            !saving &&
            selectedMiniGameId !== '' &&
            availableMiniGames.length > 0;

        return (
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle>
                    Asignar juego
                    {user && user.name ? ` a ${user.name}` : ''}
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {loading && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    py: 2
                                }}
                            >
                                <CircularProgress size={24} />
                            </Box>
                        )}

                        {error && (
                            <Typography variant="body2" color="error">
                                {error}
                            </Typography>
                        )}

                        {!loading && availableMiniGames.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                Este usuario ya tiene asignados todos los juegos
                                disponibles.
                            </Typography>
                        )}

                        {!loading && availableMiniGames.length > 0 && (
                            <FormControl fullWidth size="small">
                                <InputLabel id="assign-mg-label">
                                    Juego
                                </InputLabel>
                                <Select
                                    labelId="assign-mg-label"
                                    label="Juego"
                                    value={
                                        selectedMiniGameId !== ''
                                            ? String(selectedMiniGameId)
                                            : ''
                                    }
                                    onChange={this.handleMiniGameChange}
                                >
                                    {availableMiniGames.map((mg) => (
                                        <MenuItem
                                            key={mg.id}
                                            value={mg.id.toString()}
                                        >
                                            {mg.name || `Juego ${mg.id}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={() => this.handleSave()}
                        disabled={!canSave}
                    >
                        {saving ? 'Asignando...' : 'Asignar'}
                    </Button>
                    <Button onClick={onClose} disabled={saving}>
                        Cancelar
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
}

// -----------------------------
// Diálogo Ver juegos del usuario
// -----------------------------

interface IViewMiniGamesDialogProps {
    open: boolean;
    user: IUserDto | null;
    onClose: () => void;
    onAssign: () => void;
}

interface IViewMiniGamesDialogState {
    miniGames: IMiniGameDto[];
    userMiniGames: IUserMiniGameDto[];
    loading: boolean;
    error: string | null;
}

class ViewMiniGamesDialog extends React.Component<
    IViewMiniGamesDialogProps,
    IViewMiniGamesDialogState
> {
    public constructor(props: IViewMiniGamesDialogProps) {
        super(props);

        this.state = {
            miniGames: [],
            userMiniGames: [],
            loading: false,
            error: null
        };

        this.loadData = this.loadData.bind(this);
    }

    public componentDidUpdate(prevProps: IViewMiniGamesDialogProps): void {
        if (
            this.props.open &&
            (!prevProps.open ||
                prevProps.user?.id !== this.props.user?.id)
        ) {
            this.loadData();
        }
    }

    private async loadData(): Promise<void> {
        const user = this.props.user;
        if (!user) return;

        this.setState({
            loading: true,
            error: null,
            miniGames: [],
            userMiniGames: []
        });

        try {
            const [mgResponse, umgResponse] = await Promise.all([
                Api.get('/api/MiniGames'),
                Api.get(`/api/UserMiniGames/by-user/${user.id}`)
            ]);

            if (!mgResponse.ok) {
                throw new Error('No se pudieron cargar los juegos.');
            }
            if (!umgResponse.ok) {
                throw new Error(
                    'No se pudieron cargar los juegos del usuario.'
                );
            }

            const miniGames: IMiniGameDto[] = await mgResponse.json();
            const userMiniGames: IUserMiniGameDto[] =
                await umgResponse.json();

            miniGames.sort((a, b) => {
                if (a.displayOrder !== b.displayOrder) {
                    return a.displayOrder - b.displayOrder;
                }
                const an = (a.name || '').toLowerCase();
                const bn = (b.name || '').toLowerCase();
                return an.localeCompare(bn);
            });

            this.setState({
                miniGames,
                userMiniGames,
                loading: false,
                error: null
            });
        } catch (error: any) {
            this.setState({
                loading: false,
                error:
                    error.message ||
                    'Error al cargar los juegos del usuario.'
            });
        }
    }

    private getUserMiniGamesWithNames(): IMiniGameDto[] {
        const { miniGames, userMiniGames } = this.state;
        const ids = new Set(userMiniGames.map((u) => u.miniGameId));
        return miniGames.filter((mg) => ids.has(mg.id));
    }

    public render(): React.ReactElement<IViewMiniGamesDialogProps> {
        const { open, onClose, onAssign, user } = this.props;
        const { loading, error } = this.state;

        const mgList = this.getUserMiniGamesWithNames();

        return (
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle>
                    Juegos asignados
                    {user && user.name ? ` a ${user.name}` : ''}
                </DialogTitle>
                <DialogContent dividers>
                    {loading && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                py: 2
                            }}
                        >
                            <CircularProgress size={24} />
                        </Box>
                    )}

                    {error && (
                        <Typography variant="body2" color="error">
                            {error}
                        </Typography>
                    )}

                    {!loading && !error && mgList.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                            Este usuario no tiene juegos asignados.
                        </Typography>
                    )}

                    {!loading && !error && mgList.length > 0 && (
                        <List dense>
                            {mgList.map((mg) => (
                                <ListItem key={mg.id}>
                                    <ListItemIcon>
                                        <SportsGolfOutlined />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={mg.name || `Juego ${mg.id}`}
                                        secondary={
                                            mg.description || `ID: ${mg.id}`
                                        }
                                    />
                                </ListItem>
                            ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant='contained' onClick={onAssign}>Asignar juego</Button>
                    <Button onClick={onClose}>Cerrar</Button>
                </DialogActions>
            </Dialog>
        );
    }
}

// -----------------------------
// Página principal de Users
// -----------------------------

export interface IUsersPageProps { }

export interface IUsersPageState {
    users: IUserDto[];
    loading: boolean;
    error: string | null;
    selection: GridRowSelectionModel;
    searchText: string;
    dialogOpen: boolean;
    dialogMode: 'create' | 'edit';
    editingUser: IUserDto | null;
    assignMiniGameOpen: boolean;
    viewMiniGamesOpen: boolean;
    selectedUserForMiniGames: IUserDto | null;
}

export default class UsersPage extends React.Component<
    IUsersPageProps,
    IUsersPageState
> {
    public constructor(props: IUsersPageProps) {
        super(props);

        this.state = {
            users: [],
            loading: false,
            error: null,
            selection: { type: "include", ids: new Set() as Set<GridRowId> },
            searchText: '',
            dialogOpen: false,
            dialogMode: 'create',
            editingUser: null,
            assignMiniGameOpen: false,
            viewMiniGamesOpen: false,
            selectedUserForMiniGames: null
        };

        this.loadUsers = this.loadUsers.bind(this);
        this.handleCreate = this.handleCreate.bind(this);
        this.handleEdit = this.handleEdit.bind(this);
        this.handleDelete = this.handleDelete.bind(this);
        this.handleSearchChange = this.handleSearchChange.bind(this);
        this.handleDialogClose = this.handleDialogClose.bind(this);
        this.handleDialogSaved = this.handleDialogSaved.bind(this);
        this.handleOpenAssignMiniGame =
            this.handleOpenAssignMiniGame.bind(this);
        this.handleOpenViewMiniGames =
            this.handleOpenViewMiniGames.bind(this);
        this.handleAssignMiniGameClosed =
            this.handleAssignMiniGameClosed.bind(this);
        this.handleViewMiniGamesClosed =
            this.handleViewMiniGamesClosed.bind(this);
        this.handleMiniGameAssigned =
            this.handleMiniGameAssigned.bind(this);
        this.renderToolbar = this.renderToolbar.bind(this);
    }

    public componentDidMount(): void {
        this.loadUsers();
    }

    private async loadUsers(): Promise<void> {
        this.setState({ loading: true, error: null });

        try {
            const response = await Api.get('/api/Users');

            if (!response.ok) {
                throw new Error('No se pudo cargar la lista de usuarios.');
            }

            const data: IUserDto[] = await response.json();

            // Opcional: ordenar por nombre
            data.sort((a, b) => {
                const an = (a.name || '').toLowerCase();
                const bn = (b.name || '').toLowerCase();
                return an.localeCompare(bn);
            });

            this.setState({
                users: data,
                loading: false,
                error: null,
                selection: { type: "include", ids: new Set() as Set<GridRowId> }
            });
        } catch (error: any) {
            this.setState({
                loading: false,
                error: error.message || 'Error al cargar los usuarios.'
            });
        }
    }

    private handleCreate(): void {
        this.setState({
            dialogOpen: true,
            dialogMode: 'create',
            editingUser: null
        });
    }

    private handleEdit(): void {
        const { selection, users } = this.state;
        const ids = Array.from(selection.ids);

        if (ids.length !== 1) {
            return;
        }

        const id = ids[0] as number;
        const user = users.find((u) => u.id === id) || null;

        if (!user) {
            alert('No se ha encontrado el usuario seleccionado.');
            return;
        }

        this.setState({
            dialogOpen: true,
            dialogMode: 'edit',
            editingUser: user
        });
    }

    private async handleDelete(): Promise<void> {
        const { selection } = this.state;

        const ids = Array.from(selection.ids);

        if (ids.length === 0) {
            return;
        }

        const confirmar = window.confirm(
            `¿Seguro que deseas eliminar ${ids.length} usuario(s)?`
        );
        if (!confirmar) return;

        try {
            for (const id of ids) {
                const response = await Api.delete(`/api/Users/${id}`);
                if (!response.ok) {
                    throw new Error(
                        `No se pudo eliminar el usuario con ID ${id}.`
                    );
                }
            }

            await this.loadUsers();
        } catch (error: any) {
            alert(error.message || 'Error al eliminar usuarios.');
        }
    }

    private handleSearchChange(
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ): void {
        this.setState({ searchText: event.target.value });
    }

    private handleDialogClose(): void {
        this.setState({
            dialogOpen: false,
            editingUser: null
        });
    }

    private async handleDialogSaved(): Promise<void> {
        this.setState({
            dialogOpen: false,
            editingUser: null
        });
        await this.loadUsers();
    }

    private getSelectedSingleUser(): IUserDto | null {
        const { selection, users } = this.state;
        const ids = Array.from(selection.ids);

        if (ids.length !== 1) {
            return null;
        }

        const id = ids[0] as number;
        return users.find((u) => u.id === id) || null;
    }

    private handleOpenAssignMiniGame(): void {
        const user = this.getSelectedSingleUser();
        if (!user) {
            alert(
                'Selecciona un único usuario para asignar juegos.'
            );
            return;
        }

        this.setState({
            assignMiniGameOpen: true,
            selectedUserForMiniGames: user
        });
    }

    private handleOpenViewMiniGames(): void {
        const user = this.getSelectedSingleUser();
        if (!user) {
            alert(
                'Selecciona un único usuario para ver sus juegos.'
            );
            return;
        }

        this.setState({
            viewMiniGamesOpen: true,
            selectedUserForMiniGames: user
        });
    }

    private handleAssignMiniGameClosed(): void {
        this.setState({
            assignMiniGameOpen: false,
            selectedUserForMiniGames: null
        });
    }

    private handleViewMiniGamesClosed(): void {
        this.setState({
            viewMiniGamesOpen: false,
            selectedUserForMiniGames: null
        });
    }

    private handleMiniGameAssigned(): void {
        this.setState({
            assignMiniGameOpen: false
        });
        // No necesitamos recargar usuarios, sólo se han modificado relaciones de juegos.
        // Mantenemos el usuario seleccionado para posibles futuras acciones, si quieres.
    }

    private matchesSearch(user: IUserDto, term: string): boolean {
        const q = term.trim().toLowerCase();
        if (!q) return true;

        const parts: string[] = [];

        if (user.name) parts.push(user.name);
        if (user.email) parts.push(user.email);
        if (user.role) parts.push(user.role);

        return parts.some((p) => p.toLowerCase().includes(q));
    }

    private formatDate(dateString: string): string {
        const d = new Date(dateString);
        if (Number.isNaN(d.getTime())) return dateString;
        return d.toLocaleString();
    }

    private renderToolbar(): React.ReactNode {
        const { searchText, selection } = this.state;
        const ids = Array.from(selection.ids);

        const selectionCount = ids.length;
        const canEdit = selectionCount === 1;
        const canDelete = selectionCount >= 1;
        const canAssignMiniGame = selectionCount === 1;
        const canViewMiniGames = selectionCount === 1;

        return (
            <>
                <Button
                    variant="contained"
                    size='small'
                    startIcon={<AddIcon />}
                    onClick={this.handleCreate}
                >
                    Nuevo
                </Button>

                <Button
                    variant="text"
                    size='small'
                    startIcon={<EditIcon />}
                    onClick={this.handleEdit}
                    disabled={!canEdit}
                    sx={{ ml: 1 }}
                >
                    Editar
                </Button>

                <Button
                    variant="text"
                    size='small'
                    startIcon={<DeleteIcon />}
                    onClick={this.handleDelete}
                    disabled={!canDelete}
                    sx={{ ml: 1 }}
                >
                    Eliminar
                </Button>

                <Button
                    variant="text"
                    size='small'
                    startIcon={<VisibilityIcon />}
                    onClick={this.handleOpenViewMiniGames}
                    disabled={!canViewMiniGames}
                    sx={{ ml: 1 }}
                >
                    Juegos
                </Button>

                <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Buscar usuario..."
                    value={searchText}
                    onChange={this.handleSearchChange}
                    sx={{
                        ml: 'auto',
                        minWidth: { xs: '100%', sm: 220, md: 260 },
                        mt: { xs: 1, sm: 0 }
                    }}
                />
            </>
        );
    }

    private renderGrid(): React.ReactNode {
        const { users, loading, error, selection, searchText } = this.state;

        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (error) {
            return (
                <Typography color="error" variant="body1">
                    {error}
                </Typography>
            );
        }

        if (!users || users.length === 0) {
            return (
                <Typography variant="body1" color="text.secondary">
                    No hay usuarios todavía.
                </Typography>
            );
        }

        const filtered = users.filter((u) =>
            this.matchesSearch(u, searchText)
        );

        const columns: GridColDef[] = [
            {
                field: 'name',
                headerName: 'Nombre',
                flex: 1,
                minWidth: 160
            },
            {
                field: 'email',
                headerName: 'Email',
                flex: 1,
                minWidth: 200
            },
            {
                field: 'role',
                headerName: 'Rol',
                width: 180
            },
            {
                field: 'isActive',
                headerName: 'Activo',
                width: 120,
                valueFormatter: (params) =>
                    params ? 'Sí' : 'No'
            },
            {
                field: 'createdAt',
                headerName: 'Creado',
                width: 200,
                valueFormatter: (params) =>
                    this.formatDate(params as string)
            }
        ];

        return (
            <Box sx={{ width: '100%' }}>
                <DataGrid
                    rows={filtered}
                    columns={columns}
                    getRowId={(row) => row.id}
                    autoHeight
                    checkboxSelection
                    rowSelectionModel={selection}
                    onRowSelectionModelChange={(model) =>
                        this.setState({ selection: model })
                    }
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{
                        pagination: {
                            paginationModel: { pageSize: 25, page: 0 }
                        }
                    }}
                    sx={{
                        mt: 1,
                        '& .MuiDataGrid-columnHeaders': { fontWeight: 600 },
                        '& .MuiDataGrid-row:hover': {
                            backgroundColor: 'action.hover'
                        }
                    }}
                />
            </Box>
        );
    }

    public render(): React.ReactElement<IUsersPageProps> {
        const {
            dialogOpen,
            dialogMode,
            editingUser,
            assignMiniGameOpen,
            viewMiniGamesOpen,
            selectedUserForMiniGames
        } = this.state;

        return (
            <>
                <AdminLayout
                    entityTitle="Usuarios"
                    entityIcon={<PeopleAltIcon />}
                    entityToolbar={this.renderToolbar()}
                >
                    {this.renderGrid()}
                </AdminLayout>

                <UserDialog
                    open={dialogOpen}
                    mode={dialogMode}
                    user={editingUser}
                    onClose={this.handleDialogClose}
                    onSaved={this.handleDialogSaved}
                />

                <AssignMiniGameDialog
                    open={assignMiniGameOpen}
                    user={selectedUserForMiniGames}
                    onClose={this.handleAssignMiniGameClosed}
                    onAssigned={this.handleMiniGameAssigned}
                />

                <ViewMiniGamesDialog
                    open={viewMiniGamesOpen}
                    user={selectedUserForMiniGames}
                    onClose={this.handleViewMiniGamesClosed}
                    onAssign={this.handleOpenAssignMiniGame}
                />
            </>
        );
    }
}
